import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Inbox,
  Send,
  FileEdit,
  Star,
  Archive,
  AlertTriangle,
  Trash2,
  Plus,
  ChevronDown,
  ChevronRight,
  Settings,
  HelpCircle,
  Tag,
  FolderPlus,
  Folder,
  Menu,
  X,
  HardDrive,
  ExternalLink,
  MoreHorizontal,
  Edit2,
  Trash,
  Crosshair
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Progress } from '../ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { cn } from '../../lib/utils';
import { useTheme } from '../../context/ThemeContext';

const IDRIVE_PARTNER_URL = 'https://console.idrivee2.com/partner/a2gdesigns';

const FOLDER_COLORS = [
  '#6D4AFF', '#8B5CF6', '#EC4899', '#EF4444', '#F97316', 
  '#EAB308', '#22C55E', '#14B8A6', '#06B6D4', '#3B82F6'
];

const iconMap = {
  Inbox: Inbox,
  Send: Send,
  FileEdit: FileEdit,
  Star: Star,
  Archive: Archive,
  AlertTriangle: AlertTriangle,
  Trash2: Trash2,
  Folder: Folder,
};

export default function Sidebar({ 
  activeFolder, 
  onFolderChange, 
  onCompose,
  collapsed,
  onToggleCollapse,
  isMobile,
  folders = [],
  labels = [],
  customFolders = [],
  onCreateFolder,
  onDeleteFolder,
  user,
  onOpenStorageSettings,
  onDropEmails
}) {
  const navigate = useNavigate();
  const { isDarkMode, primaryColor } = useTheme();
  const [labelsExpanded, setLabelsExpanded] = useState(true);
  const [foldersExpanded, setFoldersExpanded] = useState(true);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState('#6D4AFF');
  const [creating, setCreating] = useState(false);

  const storageUsed = parseFloat(user?.storage?.used || 0);
  const storageTotal = parseFloat(user?.storage?.total || 15);
  const storagePercent = (storageUsed / storageTotal) * 100;

  // Theme-aware classes
  const sidebarBg = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-white';
  const borderColor = isDarkMode ? 'border-[#2d2d44]' : 'border-gray-200';
  const textColor = isDarkMode ? 'text-gray-300' : 'text-gray-600';
  const textHover = isDarkMode ? 'hover:text-white' : 'hover:text-gray-900';
  const buttonHover = isDarkMode ? 'hover:bg-[#2d2d44]' : 'hover:bg-gray-100';
  const activeBg = isDarkMode ? 'bg-[#2d2d44]' : 'bg-gray-100';
  const activeText = isDarkMode ? 'text-white' : 'text-gray-900';
  const mutedText = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const separatorBg = isDarkMode ? 'bg-[#2d2d44]' : 'bg-gray-200';
  
  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !onCreateFolder) return;
    setCreating(true);
    try {
      await onCreateFolder(newFolderName, newFolderColor);
      setNewFolderName('');
      setNewFolderColor('#6D4AFF');
      setShowNewFolderDialog(false);
    } catch (err) {
      console.error('Failed to create folder:', err);
    } finally {
      setCreating(false);
    }
  };

  // Drag handlers for folders
  const handleDragOver = (e, folderId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolder(folderId);
  };

  const handleDragLeave = () => {
    setDragOverFolder(null);
  };

  const handleDrop = (e, folderId) => {
    e.preventDefault();
    setDragOverFolder(null);
    
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (data.emailIds && data.emailIds.length > 0 && onDropEmails) {
        onDropEmails(data.emailIds, folderId);
      }
    } catch (err) {
      console.error('Drop error:', err);
    }
  };

  return (
    <aside 
      className={cn(
        `flex flex-col ${sidebarBg} border-r ${borderColor} transition-all duration-300`,
        // Desktop: show collapsed (w-16) or expanded (w-64)
        !isMobile && collapsed ? "w-16" : !isMobile ? "w-64" : "",
        // Mobile: full overlay when open, completely hidden when closed
        isMobile && "fixed inset-y-0 left-0 z-50 w-72",
        isMobile && collapsed && "-translate-x-full"
      )}
    >
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        {(!collapsed || isMobile) && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-[#6D4AFF] to-[#8B5CF6] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">M</span>
            </div>
            <span className={`${activeText} font-semibold text-lg`}>MyPatriotMail</span>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className={`${mutedText} ${textHover} ${buttonHover}`}
        >
          {collapsed && !isMobile ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
        </Button>
      </div>

      {/* Compose Button */}
      <div className={cn("px-3 mb-2", collapsed && !isMobile && "px-2")}>
        <Button
          onClick={onCompose}
          style={{ backgroundColor: primaryColor }}
          className={cn(
            "hover:opacity-90 text-white font-medium transition-all",
            collapsed && !isMobile ? "w-10 h-10 p-0" : "w-full"
          )}
        >
          <Plus className="w-5 h-5" />
          {(!collapsed || isMobile) && <span className="ml-2">New message</span>}
        </Button>
      </div>

      {/* My Wall Button - Encrypted green outline and glow */}
      <div className={cn("px-3 mb-4", collapsed && !isMobile && "px-2")}>
        <Button
          onClick={() => navigate('/wall')}
          variant="outline"
          data-testid="my-wall-btn"
          className={cn(
            "relative overflow-hidden group transition-all duration-300",
            "border-emerald-500/60 bg-emerald-500/5",
            "hover:border-emerald-400 hover:bg-emerald-500/10",
            "hover:shadow-[0_0_25px_rgba(16,185,129,0.35)]",
            "shadow-[0_0_12px_rgba(16,185,129,0.15)]",
            collapsed && !isMobile ? "w-10 h-10 p-0" : "w-full"
          )}
        >
          {/* Scan line animation on hover */}
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-400/0 via-emerald-400/10 to-emerald-400/0 translate-y-full group-hover:translate-y-[-100%] transition-transform duration-1000" />
          
          <span className="relative z-10 text-emerald-400 text-sm font-mono tracking-wider flex items-center gap-2">
            <svg viewBox="0 0 16 16" className="w-4 h-4 fill-current" aria-hidden="true">
              <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
            </svg>
            {(!collapsed || isMobile) && "MY WALL"}
          </span>
          
          {/* Pulsing glow dot */}
          {(!collapsed || isMobile) && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)] animate-pulse" />
          )}
        </Button>
      </div>

      {/* Sentinel Mail Button */}
      <div className={cn("px-3 mb-4", collapsed && !isMobile && "px-2")}>
        <Button
          onClick={() => navigate('/sentinel')}
          variant="outline"
          data-testid="sentinel-mail-btn"
          className={cn(
            "relative overflow-hidden group transition-all duration-300",
            "border-[#6D4AFF]/50 bg-[#6D4AFF]/5",
            "hover:border-[#6D4AFF] hover:bg-[#6D4AFF]/10",
            "hover:shadow-[0_0_20px_rgba(109,74,255,0.3)]",
            collapsed && !isMobile ? "w-10 h-10 p-0" : "w-full"
          )}
        >
          <span className="relative z-10 text-[#6D4AFF] text-sm font-mono tracking-wider flex items-center gap-2">
            <Crosshair className="w-4 h-4" />
            {(!collapsed || isMobile) && "SENTINEL"}
          </span>
        </Button>
      </div>

      <ScrollArea className="flex-1">
        {/* Main Folders */}
        <nav className="px-2">
          <TooltipProvider>
          {folders.map((folder) => {
            const Icon = iconMap[folder.icon] || Inbox;
            const isActive = activeFolder === folder.id;
            const isDragOver = dragOverFolder === folder.id;
            const isTrash = folder.id === 'trash';
            
            const folderButton = (
              <button
                key={folder.id}
                onClick={() => onFolderChange(folder.id)}
                onDragOver={(e) => handleDragOver(e, folder.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, folder.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-all",
                  `${textColor} ${textHover} ${buttonHover}`,
                  isActive && `${activeBg} ${activeText}`,
                  isDragOver && "bg-[#6D4AFF]/30 border-2 border-dashed border-[#6D4AFF] scale-105",
                  collapsed && !isMobile && "justify-center px-2"
                )}
              >
                <Icon className={cn(
                  "w-5 h-5 flex-shrink-0",
                  folder.id === 'inbox' && isActive && "text-[#6D4AFF]",
                  folder.id === 'starred' && "text-yellow-500",
                  folder.id === 'spam' && "text-orange-500",
                  folder.id === 'trash' && "text-red-400",
                  isDragOver && "text-[#6D4AFF]"
                )} />
                {(!collapsed || isMobile) && (
                  <>
                    <span className="flex-1 text-left text-sm">{folder.name}</span>
                    {folder.count > 0 && (
                      <span className={cn(
                        "text-xs px-2 py-0.5 rounded-full",
                        isActive ? "bg-[#6D4AFF] text-white" : `${isDarkMode ? 'bg-[#2d2d44]' : 'bg-gray-200'} ${mutedText}`
                      )}>
                        {folder.count}
                      </span>
                    )}
                  </>
                )}
              </button>
            );
            
            // Wrap trash folder with tooltip
            if (isTrash) {
              return (
                <Tooltip key={folder.id}>
                  <TooltipTrigger asChild>
                    {folderButton}
                  </TooltipTrigger>
                  <TooltipContent 
                    side="right" 
                    className={`${isDarkMode ? 'bg-[#2d2d44] text-white border-[#3d3d54]' : 'bg-white text-gray-900 border-gray-200'} max-w-xs`}
                  >
                    <p className="text-sm">All emails in trash will be permanently deleted when you log out</p>
                  </TooltipContent>
                </Tooltip>
              );
            }
            
            return folderButton;
          })}
          </TooltipProvider>
        </nav>

        {/* Custom Folders Section */}
        {(!collapsed || isMobile) && (
          <>
            <Separator className={`my-4 ${separatorBg}`} />

            <div className="px-2">
              <div className="flex items-center justify-between px-3 py-2">
                <button
                  onClick={() => setFoldersExpanded(!foldersExpanded)}
                  className={`flex items-center gap-2 ${mutedText} ${textHover} transition-colors`}
                >
                  {foldersExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                  <FolderPlus className="w-4 h-4" />
                  <span className="text-sm font-medium">Folders</span>
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowNewFolderDialog(true)}
                  className={`h-6 w-6 ${mutedText} ${textHover} ${buttonHover}`}
                  title="Add folder"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {foldersExpanded && (
                <div className="mt-1 space-y-1">
                  {customFolders.length === 0 ? (
                    <p className={`text-xs ${mutedText} px-3 py-2`}>No custom folders</p>
                  ) : (
                    customFolders.map((folder) => {
                      const isActive = activeFolder === `custom_${folder.id}`;
                      const isDragOver = dragOverFolder === `custom_${folder.id}`;
                      
                      return (
                        <div
                          key={folder.id}
                          className={cn(
                            "group flex items-center gap-3 px-3 py-2 rounded-lg transition-all cursor-pointer",
                            `${textColor} ${textHover} ${buttonHover}`,
                            isActive && `${activeBg} ${activeText}`,
                            isDragOver && "bg-[#6D4AFF]/30 border-2 border-dashed border-[#6D4AFF]"
                          )}
                          onClick={() => onFolderChange(`custom_${folder.id}`)}
                          onDragOver={(e) => handleDragOver(e, `custom_${folder.id}`)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, `custom_${folder.id}`)}
                        >
                          <Folder 
                            className="w-4 h-4 flex-shrink-0" 
                            style={{ color: folder.color }}
                          />
                          <span className="flex-1 text-left text-sm truncate">{folder.name}</span>
                          {folder.count > 0 && (
                            <span className={cn(
                              "text-xs px-2 py-0.5 rounded-full",
                              isActive ? "bg-[#6D4AFF] text-white" : `${isDarkMode ? 'bg-[#2d2d44]' : 'bg-gray-200'} ${mutedText}`
                            )}>
                              {folder.count}
                            </span>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className={`h-6 w-6 ${mutedText} opacity-0 group-hover:opacity-100 transition-opacity`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className={`${isDarkMode ? 'bg-[#1a1a2e] border-[#2d2d44]' : 'bg-white border-gray-200'}`}>
                              <DropdownMenuItem 
                                className={`text-red-400 hover:text-red-300 ${isDarkMode ? 'focus:bg-[#2d2d44]' : 'focus:bg-gray-100'} cursor-pointer`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteFolder && onDeleteFolder(folder.id);
                                }}
                              >
                                <Trash className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {(!collapsed || isMobile) && labels.length > 0 && (
          <>
            <Separator className={`my-4 ${separatorBg}`} />

            {/* Labels Section */}
            <div className="px-2">
              <button
                onClick={() => setLabelsExpanded(!labelsExpanded)}
                className={`w-full flex items-center gap-2 px-3 py-2 ${mutedText} ${textHover} transition-colors`}
              >
                {labelsExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
                <Tag className="w-4 h-4" />
                <span className="text-sm font-medium">Labels</span>
              </button>

              {labelsExpanded && (
                <div className="mt-1 space-y-1">
                  {labels.map((label) => (
                    <button
                      key={label.id}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${textColor} ${textHover} ${buttonHover} transition-colors`}
                    >
                      <span
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: label.color }}
                      />
                      <span className="text-sm">{label.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </ScrollArea>

      {/* Footer */}
      {(!collapsed || isMobile) && (
        <div className={`p-4 border-t ${borderColor}`}>
          {/* Storage */}
          <div className="mb-4">
            <div className={`flex justify-between text-xs ${mutedText} mb-2`}>
              <span>Storage</span>
              <span>{user?.storage?.used || 0} / {user?.storage?.total || 15} {user?.storage?.unit || 'GB'}</span>
            </div>
            <Progress value={storagePercent} className={`h-1.5 ${isDarkMode ? 'bg-[#2d2d44]' : 'bg-gray-200'}`} />
            <button
              onClick={() => window.open(IDRIVE_PARTNER_URL, '_blank')}
              className="flex items-center gap-1 text-xs text-[#6D4AFF] hover:text-[#8B5CF6] mt-2 transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              Get more storage
            </button>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={onOpenStorageSettings}
              className={`${mutedText} ${textHover} ${buttonHover}`}
              title="Storage Settings"
            >
              <HardDrive className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/settings')}
              className={`${mutedText} ${textHover} ${buttonHover}`}
              title="Account Settings"
            >
              <Settings className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={`${mutedText} ${textHover} ${buttonHover}`}
              title="Help & FAQ"
              onClick={() => navigate('/help')}
            >
              <HelpCircle className="w-5 h-5" />
            </Button>
          </div>
        </div>
      )}

      {/* New Folder Dialog */}
      <Dialog open={showNewFolderDialog} onOpenChange={setShowNewFolderDialog}>
        <DialogContent className={`${isDarkMode ? 'bg-[#1a1a2e] border-[#2d2d44]' : 'bg-white border-gray-200'}`}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : 'text-gray-900'}>Create New Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className={`text-sm ${mutedText} mb-2 block`}>Folder Name</label>
              <Input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="My Folder"
                className={`${isDarkMode ? 'bg-[#252540] border-[#2d2d44] text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
              />
            </div>
            <div>
              <label className={`text-sm ${mutedText} mb-2 block`}>Color</label>
              <div className="flex flex-wrap gap-2">
                {FOLDER_COLORS.map((color) => (
                  <button
                    key={color}
                    onClick={() => setNewFolderColor(color)}
                    className={cn(
                      "w-8 h-8 rounded-full transition-all",
                      newFolderColor === color && "ring-2 ring-offset-2 ring-[#6D4AFF]",
                      isDarkMode ? "ring-offset-[#1a1a2e]" : "ring-offset-white"
                    )}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowNewFolderDialog(false)}
              className={isDarkMode ? 'border-[#2d2d44]' : 'border-gray-200'}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim() || creating}
              className="bg-[#6D4AFF] hover:bg-[#5B3FD9] text-white"
            >
              {creating ? 'Creating...' : 'Create Folder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </aside>
  );
}
